import { useContext, useState, useRef } from "react";
import {
  ComponentsContext,
  FiltersContext,
  IComponentsContext,
} from "@/contexts";
import { ITreeItem } from "@/interfaces";
import { FilterTypes } from "@/enums";
import { truncate } from "@/utils/stringUtils";
import { DEFAULT_FILTER_KEY } from "@/constants";
import { getTranslation, redirect } from "@/utils";
import { FilterCriteriaPanel } from "../FilterCriteriaPanel";
import { ConfirmationModal } from "@/shared-components";
import {
  MuxArrowRight3,
  MuxCopy,
  MuxExport,
  MuxPencil,
  MuxPencilRename,
  MuxPlusCircle,
  MuxSetting,
  MuxTabFilter,
  MuxTrash,
} from "@/Icons";
import { useFavoritesContext } from "@/contexts";
import { FiltersService, TreeService } from "@/services";
import { FavoriteContextMenuItem } from "./components";
import { DeletionPreventionModal } from "@/shared-components/DeletionPreventionModal";
import { useDeletionPrevention } from "./useDeletionPrevention";

interface FilterActionsMenuProps {
  data: ITreeItem;
  items: ITreeItem[];
  isSelected?: boolean;
  hasEditPermissions: boolean;
  setIsEditable: (isRename: boolean) => void;
}

export const FilterContextMenu = ({
  data,
  items,
  setIsEditable,
  hasEditPermissions,
}: FilterActionsMenuProps) => {
  const {
    scope,
    deleteFilter,
    addSubFilter,
    reloadItems,
    editView,
    moveView,
    createView,
    copyAsPtql,
  } = useContext(FiltersContext)!;
  const { favorites, reloadFavorites } = useFavoritesContext();
  const { projectId, userId, module, entity: entityName } = scope;
  const id = data.id;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const closeDeleteModal = () => setShowDeleteModal(false);

  const handleClickOnDeleteFilter = () => {
    setShowDeleteModal(true);
  };
  const [showFilterCriteria, setShowFilterCriteria] = useState(false);
  const criteriaOverlayTarget = useRef(null);
  const isDefault = id === DEFAULT_FILTER_KEY;
  const canEdit = isDefault
    ? hasEditPermissions
    : (hasEditPermissions && data?.serverObject?.is_public) ||
      data?.serverObject?.owner_id == userId;
  const noEditPermissionTooltip = getTranslation(
    "filters-tree.messages.edit-permission-required",
    "You are not allowed to edit this filter (Only Filter's Owner or Project Admins may update)",
  );
  const {
    showDeletionPreventionModal,
    deletionPreventionData,
    checkDeletionPrevention,
    closeDeletionPreventionModal,
  } = useDeletionPrevention(data.id);

  const isFavorite = favorites.some((f) => f.id == data.id);
  const filterType = FiltersService.getFilterType(data.serverObject);

  const { DropdownMenu, DropdownMenuBody, DropdownMenuItem } =
    useContext<IComponentsContext>(ComponentsContext);

  async function handleFilterDeletion() {
    try {
      closeDeleteModal();
      const canDelete = await checkDeletionPrevention();
      if (!canDelete) return;

      await deleteFilter(data.id);
      TreeService.deleteTreeItemFromState(items, data);
      reloadItems(items);
      await reloadFavorites();
      mixpanel.track("filters-tree", {
        Action: "delete-filter",
      });
    } catch (e) {
      //TODO: pop up toaster
      console.error(`handleFilterDeletion->`, e);
    }
  }

  return (
    <DropdownMenu
      // Workaround for stupid hover logic
      overlayProps={{
        offset: -10,
        placement: "bottom-end",
        flip: true,
        // Cap height to the available space so the menu scrolls instead of being cut off
        "max-size?": true,
      }}
      toggleElement={({ toggle }: { toggle: () => void }) => {
        return (
          <span
            onClick={toggle}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") toggle();
            }}
            role="button"
            tabIndex={0}
          >
            <MuxSetting />
          </span>
        );
      }}
    >
      <DropdownMenuBody>
        {id === DEFAULT_FILTER_KEY ? (
          <DropdownMenuItem
            disabled={!canEdit}
            onClick={() =>
              redirect(
                `/projects/${projectId}/custom_views/default/edit?entity=${entityName}`,
              )
            }
            icon={<MuxPencil />}
            text={getTranslation("generic.button.edit", "Edit")}
            tooltip={!canEdit ? noEditPermissionTooltip : undefined}
          />
        ) : filterType === FilterTypes.AUTO_FILTER ? (
          <>
            <FavoriteContextMenuItem isFavorite={isFavorite} data={data} />
            {copyAsPtql && (
              <DropdownMenuItem
                onClick={() =>
                  copyAsPtql(
                    String(data.id),
                    data.serverObject?.auto_filter ?? undefined,
                  )
                }
                icon={<MuxCopy />}
                text={getTranslation(
                  "filters-tree.context-menu.copy-as-ptql",
                  "Copy as PTQL",
                )}
              />
            )}
          </>
        ) : (
          <>
            <DropdownMenuItem
              disabled={!canEdit}
              onClick={() => {
                mixpanel.track("filters-tree", {
                  Action: "edit-filter",
                });
                if (editView) {
                  editView(id);
                } else {
                  redirect(`/projects/${projectId}/custom_views/${id}/edit`);
                }
              }}
              icon={<MuxPencil />}
              text={getTranslation("generic.button.edit", "Edit")}
              tooltip={!canEdit ? noEditPermissionTooltip : undefined}
            />
            <DropdownMenuItem
              disabled={!canEdit}
              onClick={() => {
                mixpanel.track("filters-tree", {
                  Action: "rename-filter",
                });
                setIsEditable(true);
              }}
              icon={<MuxPencilRename />}
              text={getTranslation(
                "filters-tree.context-menu.rename",
                "Rename",
              )}
              tooltip={!canEdit ? noEditPermissionTooltip : undefined}
            />
            {moveView && (
              <DropdownMenuItem
                disabled={!canEdit}
                onClick={() => {
                  mixpanel.track("filters-tree", {
                    Action: "move-filter",
                  });
                  moveView(id);
                }}
                icon={<MuxArrowRight3 />}
                text={getTranslation("filters-tree.context-menu.move", "Move")}
                tooltip={!canEdit ? noEditPermissionTooltip : undefined}
              />
            )}
            <FavoriteContextMenuItem isFavorite={isFavorite} data={data} />
            {data?.extra?.filterType === FilterTypes.USER &&
              (createView ? (
                <>
                  <DropdownMenuItem
                    disabled={!canEdit}
                    onClick={() => {
                      mixpanel.track("filters-tree", {
                        Action: "add-sub-filter",
                        "Filter Type": "manual",
                      });
                      TreeService.expandTreeNode(scope, id);
                      createView(id, "manual");
                    }}
                    icon={<MuxPlusCircle />}
                    text={getTranslation(
                      "filters-tree.context-menu.add-manual-sub-filter",
                      "Add Manual Sub Filter",
                    )}
                    tooltip={!canEdit ? noEditPermissionTooltip : undefined}
                  />
                  <DropdownMenuItem
                    disabled={!canEdit}
                    onClick={() => {
                      mixpanel.track("filters-tree", {
                        Action: "add-sub-filter",
                        "Filter Type": "auto",
                      });
                      TreeService.expandTreeNode(scope, id);
                      createView(id, "auto");
                    }}
                    icon={<MuxPlusCircle />}
                    text={getTranslation(
                      "filters-tree.context-menu.add-auto-sub-filter",
                      "Add Auto Sub Filter",
                    )}
                    tooltip={!canEdit ? noEditPermissionTooltip : undefined}
                  />
                </>
              ) : (
                <DropdownMenuItem
                  disabled={!canEdit}
                  onClick={() => {
                    mixpanel.track("filters-tree", {
                      Action: "add-sub-filter",
                    });
                    addSubFilter(id);
                  }}
                  icon={<MuxPlusCircle />}
                  text={getTranslation(
                    "filters-tree.context-menu.add-sub-filter",
                    "Add Sub Filter",
                  )}
                  tooltip={!canEdit ? noEditPermissionTooltip : undefined}
                />
              ))}
            <DropdownMenuItem
              disabled={!canEdit}
              onClick={handleClickOnDeleteFilter}
              icon={<MuxTrash />}
              text={getTranslation("generic.button.delete", "Delete")}
              tooltip={!canEdit ? noEditPermissionTooltip : undefined}
              testId="filter-menu-delete-btn"
            />
            <ConfirmationModal
              show={showDeleteModal}
              title={getTranslation(
                "filters-tree.modal.delete-filter.title",
                "Delete Filter?",
              )}
              body={getTranslation(
                "filters-tree.modal.delete-filter.body",
                "The filter and its sub-filters will be deleted, but the data within will not be lost.",
              )}
              onYes={handleFilterDeletion}
              onNo={closeDeleteModal}
              yesLabel={getTranslation("generic.button.delete", "Delete")}
              noLabel={getTranslation("generic.button.cancel", "Cancel")}
              yesTestId="filter-delete-confirm-btn"
            />
            <DeletionPreventionModal
              show={showDeletionPreventionModal}
              onHide={closeDeletionPreventionModal}
              title={getTranslation(
                "filters-tree.modal.deletion-prevention.title",
                "Failed to Delete View",
              )}
              usedInLabel={getTranslation(
                "filters-tree.modal.deletion-prevention.used-in",
                "Please review the following items and unlink the view before continuing:",
              )}
              deletionPreventionData={deletionPreventionData}
            />
            <DropdownMenuItem
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                setShowFilterCriteria(true);
                mixpanel.track("filters-tree", {
                  Action: "show-filter-criteria",
                });
              }}
              icon={<MuxTabFilter />}
              text={
                <span
                  ref={criteriaOverlayTarget}
                  role="button"
                  tabIndex={0} // Make it focusable
                  onKeyDown={() => {}}
                >
                  {getTranslation(
                    "filters-tree.context-menu.filter-criteria",
                    "Show Filter Criteria",
                  )}
                </span>
              }
            >
              <FilterCriteriaPanel
                filterId={id}
                data={data}
                target={criteriaOverlayTarget.current}
                show={showFilterCriteria}
                onHide={() => setShowFilterCriteria(false)}
              />
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                mixpanel.track("filters-tree", {
                  Action: "export-filter",
                });
                const fields = data.serverObject?.definition || [];
                const params = {
                  export_entity: module,
                  export_cv: id,
                  export_cv_name: truncate(data.name, 40),
                  export_fields: fields.join(","),
                };
                redirect(
                  `/p/${projectId}/reports?${new URLSearchParams(params).toString()}`,
                );
              }}
              icon={<MuxExport />}
              text={getTranslation(
                "filters-tree.context-menu.export-xlsx",
                "Export to XLSX",
              )}
            />
            {copyAsPtql && filterType !== FilterTypes.AUTO_FILTER_ROOT && (
              <DropdownMenuItem
                onClick={() => copyAsPtql(String(data.id))}
                icon={<MuxCopy />}
                text={getTranslation(
                  "filters-tree.context-menu.copy-as-ptql",
                  "Copy as PTQL",
                )}
              />
            )}
          </>
        )}
      </DropdownMenuBody>
    </DropdownMenu>
  );
};
